import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import useModalA11y from '../components/useModalA11y';

import {
  getPaymentsByLibrary,
  getRefundsByLibrary,
  addRefund,
  refundPayment,
  getGatewayRefundStatus,
  getErrorMessage
} from '../../api/api';
import { readPrefetched } from '../../api/prefetchCache';
import '../lib-page-header.css';
import '../lib-typography.css';
// RefundList's modal reuses .fine-popup-* classes defined in Member.css —
// import it explicitly rather than relying on it being loaded globally by
// some other page in the same bundle.
import '../Members/Member.css';
import './RefundList.css';
import Pagination from '../components/Pagination';

const REFUNDS_RESOURCE = 'refunds';
const PAGE_SIZE = 10;

// A payment is only ever refund-eligible if payment_status = success, and —
// for an HDFC payment specifically — HDFC itself confirmed it CHARGED and
// this app already applied the business transaction for it
// (business_applied_at). A cash/manual payment has no gateway fields to
// check, so payment_status = success alone is the full picture there.
const isRefundEligiblePaymentStatus = (payment) => {
  if (payment.payment_status !== 'success') return false;

  if (payment.payment_gateway === 'hdfc') {
    return payment.gateway_status === 'CHARGED' && Boolean(payment.business_applied_at);
  }

  return true;
};

const RefundList = () => {
  useAuth();
  const libraryId = useActiveLibraryId();

  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [activePayment, setActivePayment] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [checkingId, setCheckingId] = useState(null);

  const modalContainerRef = useRef(null);
  const lastTriggerRef = useRef(null);
  // Belt-and-suspenders against a double submission slipping through before
  // React re-renders the disabled button (the `saving` state check below is
  // the primary guard) — a plain ref updates synchronously, with no render
  // in between.
  const submittingRef = useRef(false);

  // Always the real, uncached network call — refund/payment figures are
  // never trusted from a stale cache. `prefetch()` is only used to warm
  // this data before the user arrives, never to serve the page's own load.
  const fetchData = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const [paymentData, refundData] = await Promise.all([
        getPaymentsByLibrary(libraryId),
        getRefundsByLibrary(libraryId)
      ]);

      setPayments(Array.isArray(paymentData) ? paymentData : []);
      setRefunds(Array.isArray(refundData) ? refundData : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Seed instantly from a hover-triggered prefetch if one is already warm
    // (paints immediately with no loading spinner), then always still run
    // the real fetch above to reconcile with the live server state.
    const warm = readPrefetched(REFUNDS_RESOURCE, libraryId);
    if (warm) {
      const [paymentData, refundData] = warm;
      setPayments(Array.isArray(paymentData) ? paymentData : []);
      setRefunds(Array.isArray(refundData) ? refundData : []);
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const refundsByPaymentId = useMemo(() => {
    const map = new Map();
    refunds.forEach((refund) => {
      const list = map.get(refund.reference_payment_id) || [];
      list.push(refund);
      map.set(refund.reference_payment_id, list);
    });
    return map;
  }, [refunds]);

  const eligiblePayments = useMemo(() => {
    return payments
      .filter(isRefundEligiblePaymentStatus)
      .map((payment) => {
        const paymentRefunds = refundsByPaymentId.get(payment.id) || [];
        const processedTotal = paymentRefunds
          .filter((r) => r.refund_status === 'processed')
          .reduce((sum, r) => sum + Number(r.refund_amount || 0), 0);
        const refundableBalance = Number(payment.amount || 0) - processedTotal;

        return {
          ...payment,
          refunds: paymentRefunds,
          refundedTotal: processedTotal,
          refundableBalance
        };
      });
  }, [payments, refundsByPaymentId]);

  const filteredPayments = useMemo(() => {
    const keyword = search.toLowerCase();
    if (!keyword) return eligiblePayments;

    return eligiblePayments.filter((payment) => {
      return (
        String(payment.first_name || '').toLowerCase().includes(keyword) ||
        String(payment.last_name || '').toLowerCase().includes(keyword) ||
        String(payment.phone || '').toLowerCase().includes(keyword) ||
        String(payment.membership_no || '').toLowerCase().includes(keyword) ||
        String(payment.transaction_id || '').toLowerCase().includes(keyword)
      );
    });
  }, [eligiblePayments, search]);

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / PAGE_SIZE));
  const paginatedPayments = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPayments.slice(start, start + PAGE_SIZE);
  }, [filteredPayments, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const openRefundModal = (payment, triggerEl) => {
    lastTriggerRef.current = triggerEl || document.activeElement;
    setActivePayment(payment);
    setRefundAmount(String(payment.refundableBalance));
    setRefundReason('');
    setShowModal(true);
  };

  const closeRefundModal = () => {
    if (saving) return;
    setShowModal(false);
    setActivePayment(null);
  };

  const handleSubmitRefund = async () => {
    if (!activePayment || saving || submittingRef.current) return;

    const amount = Number(refundAmount);

    if (!(amount > 0)) {
      Swal.fire('Error', 'Refund amount must be greater than 0', 'error');
      return;
    }

    if (amount > activePayment.refundableBalance) {
      Swal.fire('Error', `Refund amount cannot exceed the refundable balance (${activePayment.refundableBalance})`, 'error');
      return;
    }

    if (!refundReason.trim()) {
      Swal.fire('Error', 'A refund reason is required', 'error');
      return;
    }

    try {
      submittingRef.current = true;
      setSaving(true);

      if (activePayment.payment_gateway === 'hdfc') {
        await refundPayment(activePayment.id, amount, refundReason.trim());
        Swal.fire('Refund initiated', 'The refund has been sent to HDFC and will be confirmed shortly.', 'success');
      } else {
        await addRefund({
          member_id: activePayment.member_id,
          membership_id: activePayment.membership_id,
          refund_amount: amount,
          refund_reason: refundReason.trim(),
          refund_status: 'processed',
          reference_payment_id: activePayment.id
        });
        Swal.fire('Success', 'Refund processed successfully', 'success');
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      // getErrorMessage already prefers the backend's real, safe error text
      // (error.response.data.error) over any generic fallback — see api.js.
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const handleCheckRefundStatus = async (refund) => {
    if (checkingId) return;

    try {
      setCheckingId(refund.id);
      const status = await getGatewayRefundStatus(refund.id);

      if (status.status === 'processed') {
        Swal.fire('Success', 'Refund confirmed and deposit balance updated', 'success');
      } else {
        Swal.fire('Still pending', `Refund status: ${status.status}`, 'info');
      }

      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setCheckingId(null);
    }
  };

  const getRefundStatusBadge = (status) => {
    if (status === 'processed') return 'bg-success';
    if (status === 'failed' || status === 'rejected') return 'bg-danger';
    return 'bg-warning text-dark';
  };

  useModalA11y({
    isOpen: showModal,
    onEscape: closeRefundModal,
    containerRef: modalContainerRef,
    restoreFocusRef: lastTriggerRef
  });

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-hand-holding-usd" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Refunds</h3>
            <div className="lib-page-header__subtitle">
              Full or partial refunds against successful payments
            </div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button type="button" className="btn btn-outline-secondary" onClick={fetchData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search member, mobile, membership no, transaction id..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {showLoader ? (
            <Loader />
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle lib-table-cell">
                <thead className="table-light lib-table-header">
                  <tr>
                    <th>#</th>
                    <th>Member</th>
                    <th className="refund-col-purpose">Purpose</th>
                    <th>Paid Amount</th>
                    <th>Refunded</th>
                    <th>Refundable Balance</th>
                    <th className="refund-col-mode">Mode</th>
                    <th className="refund-col-history">Refund History</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayments.length > 0 ? (
                    paginatedPayments.map((payment, index) => (
                      <tr key={payment.id}>
                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                        <td>
                          <strong>{payment.first_name} {payment.last_name}</strong>
                          <div className="small text-muted">{payment.phone}</div>
                          {payment.membership_no && (
                            <div className="small text-muted lib-ref-text">Membership: {payment.membership_no}</div>
                          )}
                        </td>
                        <td className="text-capitalize refund-col-purpose">{String(payment.payment_for || '').replace('_', ' ')}</td>
                        <td>₹{Number(payment.amount || 0)}</td>
                        <td>₹{payment.refundedTotal}</td>
                        <td>
                          <strong className={payment.refundableBalance > 0 ? 'text-success' : 'text-muted'}>
                            ₹{payment.refundableBalance}
                          </strong>
                        </td>
                        <td className="text-uppercase refund-col-mode">{payment.payment_method}</td>
                        <td className="refund-col-history">
                          {payment.refunds.length === 0 ? (
                            <span className="text-muted small">No refunds yet</span>
                          ) : (
                            <ul className="list-unstyled mb-0 small">
                              {payment.refunds.map((refund) => (
                                <li key={refund.id} className="mb-1">
                                  ₹{refund.refund_amount}{' '}
                                  <span className={`badge ${getRefundStatusBadge(refund.refund_status)}`}>
                                    {refund.refund_status}
                                  </span>
                                  {refund.refund_status !== 'processed' && refund.gateway === 'hdfc' && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 ms-1"
                                      onClick={() => handleCheckRefundStatus(refund)}
                                      disabled={checkingId === refund.id}
                                    >
                                      {checkingId === refund.id ? 'Checking...' : 'Check status'}
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td>
                          {payment.refundableBalance > 0 ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={(e) => openRefundModal(payment, e.currentTarget)}
                            >
                              Refund
                            </button>
                          ) : (
                            <span className="text-muted small">Fully refunded</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        No refund-eligible payments found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredPayments.length}
                itemLabel="payments"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {showModal && activePayment && (
        <div className="fine-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && closeRefundModal()}>
          <div
            className="fine-popup-box fine-popup-box--refund"
            ref={modalContainerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rfd-modal-title"
          >
            <div className="fine-popup-header">
              <h5 className="mb-0" id="rfd-modal-title">Refund Payment</h5>
              <button type="button" className="fine-popup-close" onClick={closeRefundModal} disabled={saving}>
                ×
              </button>
            </div>

            <div className="fine-popup-body">
              <div className="rfd-member-line">
                <strong>{activePayment.first_name} {activePayment.last_name}</strong>
                {activePayment.phone && <span className="rfd-member-sub"> · {activePayment.phone}</span>}
              </div>

              <div className="rfd-summary">
                <div className="rfd-summary__item">
                  <span className="rfd-summary__label">Paid Amount</span>
                  <span className="rfd-summary__value">₹{Number(activePayment.amount || 0)}</span>
                </div>
                <div className="rfd-summary__item">
                  <span className="rfd-summary__label">Refundable Balance</span>
                  <span className="rfd-summary__value">₹{activePayment.refundableBalance}</span>
                </div>
              </div>

              <label className="form-label lib-form-label">Refund Amount *</label>
              <div className="input-group mb-3 rfd-amount-row">
                <input
                  type="number"
                  className="form-control"
                  min="0.01"
                  max={activePayment.refundableBalance}
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  disabled={saving}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setRefundAmount(String(activePayment.refundableBalance))}
                  disabled={saving}
                >
                  Full Refund
                </button>
              </div>

              <label className="form-label lib-form-label">Refund Reason *</label>
              <textarea
                className="form-control"
                rows="3"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                disabled={saving}
                required
              />
            </div>

            <div className="fine-popup-footer">
              <button type="button" className="btn btn-light" onClick={closeRefundModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleSubmitRefund} disabled={saving}>
                {saving ? 'Processing...' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundList;
